import { type IntlShape, defineMessage } from 'react-intl'
import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'
import { isLuxPlusSubscriptionItem, isSubscriptionJoinItem } from 'lib/checkout/checkoutUtils'
import { LUXURY_PLUS } from 'luxPlus/constants/base'

type BookingInfoItem = App.Checkout.PaymentScheduleRequestInfo['bookingInfo']['items'][number]

/**
 * Lead-only items are paid upfront by the lead and never split with the group. Today that's
 * the LuxPlus membership; everything else in a Group Payment cart is split.
 * Mirrors svc-payment's GROUP_PAYMENT_LEAD_ONLY_ITEM_TYPES. Adding a lead-only type also needs
 * svc-payment's Group Payment calculation extended (see GROUP_PAYMENT_LEAD_ONLY_ITEM_TYPES).
 */
export type GroupPaymentLeadOnlyItemType = 'luxPlusMembership'

export interface GroupPaymentLeadOnlyItem {
  type: GroupPaymentLeadOnlyItemType
  amount: number
}

export function isGroupPaymentLeadOnlyCartItem(item: App.Checkout.AnyItem): boolean {
  return isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)
}

export function isGroupPaymentLeadOnlyBookingItem(item: BookingInfoItem): boolean {
  return item.type === 'subscription'
}

function toLuxPlusMembershipItems(amount: number): Array<GroupPaymentLeadOnlyItem> {
  const membershipAmount = floatify(amount)
  return membershipAmount > 0 ? [{ type: 'luxPlusMembership', amount: membershipAmount }] : []
}

export function getGroupPaymentLeadOnlyBookingItems(items: Array<BookingInfoItem>): Array<GroupPaymentLeadOnlyItem> {
  return toLuxPlusMembershipItems(sum(
    items.filter(isGroupPaymentLeadOnlyBookingItem),
    item => item.total - item.discountTotal,
  ))
}

export function getGroupPaymentLeadOnlyOrderItems(order: App.Order): Array<GroupPaymentLeadOnlyItem> {
  return toLuxPlusMembershipItems(sum(order.subscriptionItems, item => item.total))
}

export function sumGroupPaymentLeadOnlyItems(items: Array<GroupPaymentLeadOnlyItem>): number {
  return floatify(sum(items, item => item.amount))
}

const luxPlusMembershipLabel = defineMessage({
  id: 'jwn63LZN9HniNSCamUN8',
  defaultMessage: '{programName} Membership',
  description: 'Label for the LuxPlus membership line paid by the lead outside the group split on a group booking payment summary',
})

export function formatGroupPaymentLeadOnlyLabel(intl: IntlShape, type: GroupPaymentLeadOnlyItemType): string {
  switch (type) {
    case 'luxPlusMembership':
      return intl.formatMessage(luxPlusMembershipLabel, { programName: LUXURY_PLUS.PROGRAM_NAME })
  }
}
